import { R } from './constants';
import { gasDensity, polytropicIndex } from './substance';
import { q, type Recorder } from './trace';
import type { ReleaseInput, Substance } from '../model/types';

export interface ReleaseResult {
  /** Mass release rate of flammable gas or vapour, kg/s. */
  massRate: number;
  /** Volumetric release rate at ambient conditions, m³/s. */
  volumetricRate: number;
  /** Gas density at ambient conditions, kg/m³. */
  gasDensity: number;
  /** Gas releases only. */
  choked?: boolean;
  criticalPressurePa?: number;
  /** Liquid releases only: total liquid rate before vaporisation, kg/s. */
  liquidRate?: number;
}

export interface ReleaseContext {
  ambientPressurePa: number;
  ambientTemperatureK: number;
  /** Ventilation velocity used for dilution; pools evaporate at the same wind speed (B.7.3). */
  ventilationVelocityMPerS: number;
}

/** Critical pressure for choked flow (eq. B.2). */
export function criticalPressure(gamma: number, ambientPressurePa: number): number {
  return ambientPressurePa * Math.pow((gamma + 1) / 2, gamma / (gamma - 1));
}

/** Mass rate of gas through an opening, choked (eq. B.5) or not (eq. B.3). */
export function gasMassRate(
  p: { pressurePaAbs: number; temperatureK: number; holeAreaM2: number; cd: number; z: number },
  molarMass: number,
  gamma: number,
  ambientPressurePa: number,
): { massRate: number; choked: boolean; criticalPressurePa: number } {
  const { pressurePaAbs: pr, temperatureK: T, holeAreaM2: S, cd, z } = p;
  const pc = criticalPressure(gamma, ambientPressurePa);
  const base = molarMass / (z * R * T);
  if (pr > pc) {
    const massRate = cd * S * pr * Math.sqrt(gamma * base * Math.pow(2 / (gamma + 1), (gamma + 1) / (gamma - 1)));
    return { massRate, choked: true, criticalPressurePa: pc };
  }
  const ratio = ambientPressurePa / pr;
  const massRate =
    cd *
    S *
    pr *
    Math.sqrt(base * ((2 * gamma) / (gamma - 1)) * (1 - Math.pow(ratio, (gamma - 1) / gamma))) *
    Math.pow(ratio, 1 / gamma);
  return { massRate, choked: false, criticalPressurePa: pc };
}

/** Liquid release rate through an opening (eq. B.1). */
export function liquidMassRate(cd: number, holeAreaM2: number, liquidDensityKgM3: number, pressureDiffPa: number): number {
  return cd * holeAreaM2 * Math.sqrt(2 * liquidDensityKgM3 * pressureDiffPa);
}

/** Evaporation rate of a non-boiling pool (eq. B.6). */
export function poolEvaporationRate(
  windSpeedMPerS: number,
  areaM2: number,
  vapourPressurePa: number,
  molarMass: number,
  liquidTemperatureK: number,
): number {
  return (18.3e-3 * Math.pow(windSpeedMPerS, 0.78) * areaM2 * vapourPressurePa * Math.pow(molarMass, 0.667)) / (R * liquidTemperatureK);
}

export function computeRelease(
  release: ReleaseInput,
  sub: Substance,
  ctx: ReleaseContext,
  rec: Recorder,
): ReleaseResult | undefined {
  const M = sub.molarMassKgPerKmol;
  const rhoG = gasDensity(M, ctx.ambientPressurePa, ctx.ambientTemperatureK, rec);

  const finish = (massRate: number, extra: Partial<ReleaseResult> = {}): ReleaseResult => {
    const volumetricRate = massRate / rhoG;
    rec.step({
      id: 'release.volumetric',
      label: 'Volumetric release rate',
      ref: { clause: 'B.7.2.3.2', eq: 'B.4' },
      inputs: { Wg: q(massRate, 'kg/s'), ρg: q(rhoG, 'kg/m3') },
      output: { symbol: 'Qg', ...q(volumetricRate, 'm3/s') },
    });
    return { massRate, volumetricRate, gasDensity: rhoG, ...extra };
  };

  switch (release.model) {
    case 'gas': {
      const gamma = polytropicIndex(sub, rec);
      if (gamma === undefined || gamma <= 1) {
        rec.issue({ code: 'gas.gamma-missing', severity: 'error', field: 'substance.gamma', message: `${sub.name} needs a ratio of specific heats (γ) or a specific heat (cp) for a gas release.`, ref: { clause: 'B.7.2.3.1' } });
        return undefined;
      }
      if (release.pressurePaAbs <= ctx.ambientPressurePa) {
        rec.issue({ code: 'gas.pressure-too-low', severity: 'error', field: 'release.pressurePaAbs', message: 'Pressure must be above atmospheric for a gas release.', ref: { clause: 'B.7.2.3' } });
        return undefined;
      }
      if (release.holeAreaM2 <= 0 || release.cd <= 0 || release.z <= 0 || release.temperatureK <= 0) {
        rec.issue({ code: 'gas.inputs', severity: 'error', field: 'release', message: 'Hole area, discharge coefficient, compressibility and temperature must all be above zero.' });
        return undefined;
      }
      if (release.pressurePaAbs > 50e5 && release.z === 1) {
        rec.issue({ code: 'gas.z-high-pressure', severity: 'warning', field: 'release.z', message: 'Above 50 bar, use the real compressibility factor rather than Z = 1.', ref: { clause: 'B.7.2.3.1' } });
      }
      const r = gasMassRate(release, M, gamma, ctx.ambientPressurePa);
      rec.step({
        id: 'release.gas.critical-pressure',
        label: 'Critical pressure',
        ref: { clause: 'B.7.2.3.1', eq: 'B.2' },
        inputs: { pa: q(ctx.ambientPressurePa, 'Pa'), γ: q(gamma, '1') },
        output: { symbol: 'pc', ...q(r.criticalPressurePa, 'Pa') },
        notes: [{ kind: 'info', text: r.choked ? 'Pressure is above critical: choked (sonic) release.' : 'Pressure is below critical: subsonic release.' }],
      });
      rec.step({
        id: r.choked ? 'release.gas.choked' : 'release.gas.subsonic',
        label: r.choked ? 'Mass release rate, choked flow' : 'Mass release rate, subsonic flow',
        ref: r.choked ? { clause: 'B.7.2.3.3', eq: 'B.5' } : { clause: 'B.7.2.3.2', eq: 'B.3' },
        inputs: {
          Cd: q(release.cd, '1'),
          S: q(release.holeAreaM2, 'm2'),
          p: q(release.pressurePaAbs, 'Pa'),
          M: q(M, 'kg/kmol'),
          Z: q(release.z, '1'),
          T: q(release.temperatureK, 'K'),
          γ: q(gamma, '1'),
          ...(r.choked ? {} : { pa: q(ctx.ambientPressurePa, 'Pa') }),
        },
        output: { symbol: 'Wg', ...q(r.massRate, 'kg/s') },
      });
      return finish(r.massRate, { choked: r.choked, criticalPressurePa: r.criticalPressurePa });
    }

    case 'liquid': {
      const rho = sub.liquidDensityKgM3;
      if (rho === undefined) {
        rec.issue({ code: 'liquid.density-missing', severity: 'error', field: 'substance.liquidDensityKgM3', message: `${sub.name} needs a liquid density for a liquid release.` });
        return undefined;
      }
      if (release.pressureDiffPa <= 0 || release.holeAreaM2 <= 0 || release.cd <= 0) {
        rec.issue({ code: 'liquid.inputs', severity: 'error', field: 'release', message: 'Pressure difference, hole area and discharge coefficient must all be above zero.' });
        return undefined;
      }
      if (release.vapourFraction <= 0 || release.vapourFraction > 1) {
        rec.issue({ code: 'liquid.fraction', severity: 'error', field: 'release.vapourFraction', message: 'The vaporised fraction must be between 0 and 100 %.' });
        return undefined;
      }
      if (!release.vapourFractionBasis) {
        rec.issue({ code: 'liquid.fraction-basis', severity: 'warning', field: 'release.vapourFractionBasis', message: 'Record how the vaporised fraction was chosen; the standard gives no method for it.', ref: { clause: 'B.7.2.2' } });
      }
      const W = liquidMassRate(release.cd, release.holeAreaM2, rho, release.pressureDiffPa);
      rec.step({
        id: 'release.liquid',
        label: 'Liquid release rate',
        ref: { clause: 'B.7.2.2', eq: 'B.1' },
        inputs: { Cd: q(release.cd, '1'), S: q(release.holeAreaM2, 'm2'), ρ: q(rho, 'kg/m3'), Δp: q(release.pressureDiffPa, 'Pa') },
        output: { symbol: 'W', ...q(W, 'kg/s') },
      });
      const wg = W * release.vapourFraction;
      rec.step({
        id: 'release.liquid.vapour',
        label: 'Vapour formed at the source',
        ref: { clause: 'B.7.2.2' },
        inputs: { W: q(W, 'kg/s'), fraction: q(release.vapourFraction, '1') },
        output: { symbol: 'Wg', ...q(wg, 'kg/s') },
        notes: [{ kind: 'assumption', text: release.vapourFractionBasis ?? 'Vaporised fraction entered by the user.' }],
      });
      return finish(wg, { liquidRate: W });
    }

    case 'pool': {
      const vp = sub.vapourPressure;
      if (!vp) {
        rec.issue({ code: 'pool.vapour-pressure-missing', severity: 'error', field: 'substance.vapourPressure', message: `${sub.name} needs a vapour pressure for an evaporating pool.` });
        return undefined;
      }
      if (release.areaM2 <= 0) {
        rec.issue({ code: 'pool.area', severity: 'error', field: 'release.areaM2', message: 'Pool area must be above zero.' });
        return undefined;
      }
      if (Math.abs(vp.atTemperatureK - release.liquidTemperatureK) > 0.5) {
        rec.issue({ code: 'pool.vp-temperature', severity: 'warning', field: 'substance.vapourPressure', message: `The vapour pressure is given at ${(vp.atTemperatureK - 273.15).toFixed(1)} °C but the liquid is at ${(release.liquidTemperatureK - 273.15).toFixed(1)} °C. Enter the vapour pressure at the liquid temperature.`, ref: { clause: 'B.7.3' } });
      }
      const uw = release.windSpeedOverrideMPerS ?? ctx.ventilationVelocityMPerS;
      if (release.windSpeedOverrideMPerS !== undefined && Math.abs(release.windSpeedOverrideMPerS - ctx.ventilationVelocityMPerS) > 1e-9) {
        rec.issue({ code: 'pool.wind-mismatch', severity: 'warning', field: 'release.windSpeedOverrideMPerS', message: 'The wind speed over the pool differs from the ventilation velocity used for dilution. The standard asks for them to be consistent.', ref: { clause: 'B.7.3' } });
      }
      if (uw <= 0) {
        rec.issue({ code: 'pool.wind', severity: 'error', field: 'ventilation', message: 'A pool needs a wind speed above zero to evaporate in this model.' });
        return undefined;
      }
      const we = poolEvaporationRate(uw, release.areaM2, vp.pa, M, release.liquidTemperatureK);
      rec.step({
        id: 'release.pool',
        label: 'Evaporation rate of the pool',
        ref: { clause: 'B.7.3', eq: 'B.6' },
        inputs: { uw: q(uw, 'm/s'), Ap: q(release.areaM2, 'm2'), pv: q(vp.pa, 'Pa'), M: q(M, 'kg/kmol'), R: q(R, 'J/(kmol·K)'), T: q(release.liquidTemperatureK, 'K') },
        output: { symbol: 'We', ...q(we, 'kg/s') },
        notes: [{ kind: 'assumption', text: 'The liquid evaporates without boiling and the pool is not a thin surface spill.', ref: { clause: 'B.7.3' } }],
      });
      return finish(we);
    }

    case 'manual': {
      if (release.massRateKgPerS <= 0) {
        rec.issue({ code: 'manual.rate', severity: 'error', field: 'release.massRateKgPerS', message: 'Release rate must be above zero.' });
        return undefined;
      }
      if (!release.reference.trim()) {
        rec.issue({ code: 'manual.reference', severity: 'warning', field: 'release.reference', message: 'Record where this release rate comes from (datasheet, test data, calculation).', ref: { clause: '10.1' } });
      }
      rec.step({
        id: 'release.manual',
        label: 'Release rate entered',
        ref: { clause: 'B.7.1' },
        inputs: {},
        output: { symbol: 'Wg', ...q(release.massRateKgPerS, 'kg/s') },
        notes: [{ kind: 'info', text: release.reference || 'No reference given.' }],
      });
      return finish(release.massRateKgPerS);
    }
  }
}
